const EXTENSIONS = ['webp', 'png', 'jpg', 'jpeg', 'gif'];

/** A webpack context in `lazy-once` mode: `keys()` is synchronous, loading a file is not. */
type AssetContext = ((key: string) => Promise<unknown>) & { keys: () => string[] };

export type ScreenshotTheme = 'light' | 'dark';

export interface ScreenshotVariant {
  /** i18next language, e.g. `en-US` or `fr`; only the base code is used. */
  language?: string;
  theme?: ScreenshotTheme;
}

export interface ScreenshotAsset {
  url: string;
  /** Intrinsic size from manifest.json (written by the capture tool), when known. */
  width?: number;
  height?: number;
}

type Manifest = Record<string, [number, number]>;

let context: AssetContext | null = null;
try {
  // Webpack-only API; undefined under Jest, hence the guard. `lazy-once` keeps the
  // image URLs out of the main bundle: they arrive in one chunk the first time a
  // guide shows a screenshot.
  context =
    (
      require as unknown as {
        context?: (...args: unknown[]) => AssetContext;
      }
    ).context?.('./', false, /\.(png|jpe?g|webp|gif)$/i, 'lazy-once') ?? null;
} catch {
  context = null;
}

let manifest: Promise<Manifest> | null = null;
const loadManifest = (): Promise<Manifest> => {
  if (!manifest) {
    manifest = import(/* webpackChunkName: "help-screenshots" */ './manifest.json')
      .then((m) => (m as { default?: Manifest }).default ?? (m as Manifest))
      .catch(() => ({}));
  }
  return manifest;
};

/**
 * File stems to try for a key, most specific first:
 * `<key>.<lang>.<theme>`, `<key>.<lang>`, `<key>.<theme>`, `<key>`.
 */
export function screenshotCandidates(
  key: string,
  { language, theme }: ScreenshotVariant = {}
): string[] {
  const lang = (language ?? '').split(/[-_]/)[0].toLowerCase();
  const stems: string[] = [];
  if (lang && theme) stems.push(`${key}.${lang}.${theme}`);
  if (lang) stems.push(`${key}.${lang}`);
  if (theme) stems.push(`${key}.${theme}`);
  stems.push(key);
  return stems;
}

/**
 * The best `help/screenshots/<key>[.<lang>][.<theme>].<ext>` for the viewer's
 * language and theme (e.g. `./key.fr.dark.webp`), or null when the key has no
 * image. Synchronous, so a missing screenshot shows its placeholder at once.
 */
export function findScreenshotFile(
  key: string,
  variant: ScreenshotVariant = {},
  ctx: Pick<AssetContext, 'keys'> | null = context
): string | null {
  if (!ctx) return null;
  const keys = new Set(ctx.keys());
  for (const stem of screenshotCandidates(key, variant)) {
    for (const ext of EXTENSIONS) {
      const candidate = `./${stem}.${ext}`;
      if (keys.has(candidate)) return candidate;
    }
  }
  return null;
}

/** Resolves a file from `findScreenshotFile` to its URL and intrinsic size. */
export function loadScreenshot(
  file: string,
  ctx: AssetContext | null = context,
  sizes: () => Promise<Manifest> = loadManifest
): Promise<ScreenshotAsset> {
  if (!ctx) return Promise.reject(new Error('Screenshots are unavailable outside webpack'));
  return Promise.all([ctx(file), sizes()]).then(([mod, known]) => {
    const url = typeof mod === 'string' ? mod : (mod as { default: string }).default;
    const size = known[file.replace(/^\.\//, '')];
    return size ? { url, width: size[0], height: size[1] } : { url };
  });
}
